<template>
  <div class="flex flex-col gap-6">
    <div v-for="(group, idx) in steps" :key="idx" class="mfa-group">
      <!-- AND 组：必须全部验证通过 -->
      <MfaAndContent
        v-if="normalize(group.operator) === 'and'"
        :conditions="group.conditions"
        :passageKey="passageKey"
        :securityCode="securityCode"
        :make="make"
        :verifyData="verifyData"
        @changed="(oks: boolean[]) => onAndChanged(idx, oks)"
      />

      <!-- OR 组：任选一种验证方式通过 -->
      <MfaOrContent
        v-else
        :conditions="group.conditions"
        :passageKey="passageKey"
        :securityCode="securityCode"
        :make="make"
        :prefer="preferChoice"
        :verifyData="verifyData"
        @resolved="(payload) => onOrResolved(idx, payload)"
      />
    </div>

    <!-- 底部提交条：只有全部组通过才可提交 -->
    <div class="flex items-center justify-end gap-3 pt-2">
      <GtcButton type="primary" mode="light" size="lg" @click="handleCancel">
        {{ t('components.security.mfa.actions.cancel', '取消') }}
      </GtcButton>
      <GtcButton type="primary" size="lg" :disabled="!allOk" @click="handleSubmit">
        {{ t('components.security.mfa.actions.finishVerify', '确定') }}
      </GtcButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import i18n from '@/locales'
import type { Condition } from '../types'
import MfaAndContent from './MfaAndContent.vue'
import MfaOrContent from './MfaOrContent.vue'
import GtcButton from '@/components/gtc-button/GtcButton.vue'

const props = defineProps<{
  steps: { operator: string; conditions: Condition[] }[]
  passageKey: string
  securityCode: string
  make: (c: Condition) => Promise<any>
  verifyData: { email: string; phone: string; loginType: number; unauthorized: boolean }
}>()

const emit = defineEmits<{ (e: 'submit'): void; (e: 'cancel'): void }>()
const t = (i18n.global as any).t

const groupOk = ref<boolean[]>(props.steps.map(() => false))

const allOk = computed(() => groupOk.value.every(Boolean))

function normalize(op: string) {
  return String(op || '').toLowerCase()
}

function onAndChanged(groupIndex: number, oks: boolean[]) {
  groupOk.value[groupIndex] = Array.isArray(oks) && oks.length > 0 && oks.every(Boolean)
}

function onOrResolved(groupIndex: number, payload: { choice: Condition; ok: boolean }) {
  groupOk.value[groupIndex] = !!payload?.ok
}

function handleSubmit() {
  if (!allOk.value) return
  emit('submit')
}

function handleCancel() {
  emit('cancel')
}

onMounted(() => {
  groupOk.value = groupOk.value.map(() => false)
})

function preferChoice(list: Condition[]) {
  const order = [
    'biometric_validate',
    'app_validate',
    'totp_validate',
    'email_validate',
    'sms_validate',
  ]
  const rank = new Map(order.map((t, i) => [t, i]))
  return list.reduce((best, cur) => {
    const bw = rank.get(best.type) ?? Number.POSITIVE_INFINITY
    const cw = rank.get(cur.type) ?? Number.POSITIVE_INFINITY
    return cw < bw ? cur : best
  }, list[0])
}
</script>
